import React, { useState } from "react";
import PropTypes from 'prop-types';
import { Link, useNavigate } from "react-router-dom";
import { Dropdown, DropdownToggle, DropdownMenu, Row, Col, Badge, Button } from "reactstrap";
import SimpleBar from "simplebar-react";
import { withTranslation } from "react-i18next";
import { useSignalRNotifications } from "../../../context/SignalRNotificationContext";

const NotificationDropdown = props => {
  const [menu, setMenu] = useState(false);
  const navigate = useNavigate();
  const { notifications, unreadCount, markAllAsRead, clearNotifications, isConnected } = useSignalRNotifications();
  const notifList = Array.isArray(notifications) ? notifications : [];

  const handleToggle = () => {
    if (!menu && unreadCount > 0) {
      markAllAsRead();
    }
    setMenu(!menu);
  };

  const handleNotificationClick = (item) => {
    setMenu(false);
    navigate("/AssignDriver", { state: { highlightBookingId: item.bookingId } });
  };

  return (
    <React.Fragment>
      <Dropdown
        isOpen={menu}
        toggle={handleToggle}
        className="dropdown d-inline-block"
        tag="li"
      >
        <DropdownToggle
          className="btn header-item noti-icon position-relative"
          tag="button"
          id="page-header-notifications-dropdown"
          title={isConnected ? "Real-time Booking Alerts Active" : "Connecting to Alerts..."}
        >
          <i className={`bx bx-bell ${unreadCount > 0 ? "bx-tada text-danger" : ""}`} />
          {unreadCount > 0 && (
            <span className="badge bg-danger rounded-pill position-absolute" style={{ top: 12, right: 6, fontSize: "10px" }}>
              {unreadCount > 99 ? "99+" : unreadCount}
            </span>
          )}
          <span
            style={{
              position: "absolute",
              bottom: 14,
              right: 10,
              width: 7,
              height: 7,
              borderRadius: "50%",
              backgroundColor: isConnected ? "#28a745" : "#ffc107"
            }}
            title={isConnected ? "Live Connected" : "Connecting..."}
          />
        </DropdownToggle>

        <DropdownMenu className="dropdown-menu dropdown-menu-lg dropdown-menu-end p-0" style={{ width: 340 }}>
          <div className="p-3 border-bottom bg-light">
            <Row className="align-items-center">
              <Col>
                <h6 className="m-0 font-weight-bold">
                  {props.t("Ride Booking Alerts")}
                  {notifList.length > 0 && (
                    <Badge color="primary" pill className="ms-2">
                      {notifList.length}
                    </Badge>
                  )}
                </h6>
              </Col>
              {notifList.length > 0 && (
                <div className="col-auto">
                  <Button
                    color="link"
                    size="sm"
                    className="p-0 text-muted small"
                    onClick={(e) => {
                      e.stopPropagation();
                      clearNotifications();
                    }}
                  >
                    Clear All
                  </Button>
                </div>
              )}
            </Row>
          </div>

          <SimpleBar style={{ maxHeight: "300px" }}>
            {notifList && notifList.length > 0 ? (
              notifList.map((item) => (
                <div
                  key={item.id}
                  className="text-reset notification-item border-bottom p-2"
                  style={{ cursor: "pointer", transition: "background 0.2s" }}
                  onClick={() => handleNotificationClick(item)}
                >
                  <div className="d-flex align-items-start">
                    <div className="avatar-xs me-2 flex-shrink-0">
                      <span className="avatar-title bg-success rounded-circle font-size-14 text-white">
                        <i className="bx bxs-taxi" />
                      </span>
                    </div>
                    <div className="flex-grow-1 overflow-hidden">
                      <div className="d-flex justify-content-between align-items-center mb-1">
                        <span className="fw-bold font-size-12 text-primary">
                          Ride #{item.bookingId}
                        </span>
                        <span className="font-size-11 text-muted">
                          <i className="mdi mdi-clock-outline me-1" />
                          {item.time}
                        </span>
                      </div>

                      <div className="font-size-12 text-dark fw-medium mb-1">
                        👤 {item.customerName} {item.customerMobile ? `(${item.customerMobile})` : ""}
                      </div>

                      <div className="font-size-11 text-muted text-truncate mb-1">
                        📍 <b>Pickup:</b> {item.pickup}
                      </div>

                      <div className="font-size-11 text-muted text-truncate mb-1">
                        🏁 <b>Drop:</b> {item.drop}
                      </div>

                      <div className="d-flex justify-content-between align-items-center mt-1 flex-wrap gap-1">
                        <div className="d-flex align-items-center gap-1 flex-wrap">
                          <Badge color="info" className="font-size-11">
                            ₹{item.fare} • {item.km} KM
                          </Badge>
                          {(item.vehicleTypeName || item.vehicleType) && (
                            <Badge color="primary" className="font-size-11">
                              🛺 {item.vehicleTypeName || item.vehicleType}
                            </Badge>
                          )}
                        </div>
                        <Button
                          color="success"
                          size="sm"
                          style={{ fontSize: "11px", padding: "2px 8px" }}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleNotificationClick(item);
                          }}
                        >
                          Assign Driver ➔
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>
              ))
            ) : (
              <div className="text-center p-4 text-muted">
                <i className="bx bx-bell-off font-size-24 d-block mb-2 text-secondary" />
                <span className="font-size-13">No new ride bookings yet</span>
                <p className="font-size-11 text-muted mt-1 mb-0">
                  New rides will appear here in real time as soon as customers book!
                </p>
              </div>
            )}
          </SimpleBar>

          <div className="p-2 border-top d-grid">
            <Link
              className="btn btn-sm btn-primary font-size-13 text-center"
              to="/AssignDriver"
              onClick={() => setMenu(false)}
            >
              <i className="mdi mdi-car-multiple me-1" /> View All Bookings / Assign Driver
            </Link>
          </div>
        </DropdownMenu>
      </Dropdown>
    </React.Fragment>
  );
};

export default withTranslation()(NotificationDropdown);

NotificationDropdown.propTypes = {
  t: PropTypes.any
};